import type { MentionToken, ReferenceAssetKind, ReferenceImage } from '@/types/conversation-input';
import type { MentionResource } from '@/types/mention';
import { getMentionResourceMediaKind } from '@/utils/mention';

const DEDUPE_SUBMIT_ROLES = new Set(['reference_image', 'reference_video', 'reference_audio']);

export function resolveVideoReferenceIdentityKey(params: {
  resourceId?: string | null;
  url?: string | null;
}): string | null {
  const resourceId = params.resourceId?.trim();
  if (resourceId) return `id:${resourceId}`;
  const url = params.url?.trim();
  if (url) return `url:${url}`;
  return null;
}

export function resolveFrameVideoIdentityKey(frame: {
  resourceId?: string | null;
  url?: string | null;
  remoteUrl?: string | null;
}): string | null {
  return resolveVideoReferenceIdentityKey({
    resourceId: frame.resourceId,
    url: frame.remoteUrl || frame.url,
  });
}

export function resolveMentionResourceVideoIdentityKey(
  resource: Pick<MentionResource, 'submitResourceId' | 'resourceId' | 'resourceUrl'>
): string | null {
  return resolveVideoReferenceIdentityKey({
    resourceId: resource.submitResourceId ?? resource.resourceId,
    url: resource.resourceUrl,
  });
}

export function isVideoMentionToken(
  token: MentionToken,
  referenceImages: ReferenceImage[]
): boolean {
  if (token.kind === 'resource' && token.resource) {
    return (
      token.resource.type === 'video' || Boolean(token.resource.mimeType?.startsWith('video/'))
    );
  }
  if (token.kind !== 'reference-image') return false;
  const image = referenceImages.find((item) => item.id === token.referenceImageId);
  return (
    token.referenceImageKind === 'video' ||
    image?.kind === 'video' ||
    Boolean(image?.mimeType?.startsWith('video/')) ||
    Boolean(token.referenceImageMimeType?.startsWith('video/'))
  );
}

export function resolveMentionTokenVideoIdentityKey(
  token: MentionToken,
  referenceImages: ReferenceImage[]
): string | null {
  if (!isVideoMentionToken(token, referenceImages)) return null;
  if (token.kind === 'resource' && token.resource) {
    return resolveMentionResourceVideoIdentityKey(token.resource);
  }
  const image = referenceImages.find((item) => item.id === token.referenceImageId);
  return resolveVideoReferenceIdentityKey({
    resourceId: image?.resourceId,
    url: image?.remoteUrl || image?.url || token.referenceImageUrl,
  });
}

export function countUniqueVideoReferenceKeys(keys: Array<string | null | undefined>): number {
  const seen = new Set<string>();
  let anonymous = 0;
  for (const key of keys) {
    if (key) {
      seen.add(key);
    } else {
      anonymous += 1;
    }
  }
  return seen.size + anonymous;
}

function resolveMentionTokenMediaKind(
  token: MentionToken,
  referenceImages: ReferenceImage[]
): ReferenceAssetKind | null {
  if (token.kind === 'reference-image') {
    const image = referenceImages.find((item) => item.id === token.referenceImageId);
    return (
      token.referenceImageKind ??
      image?.kind ??
      (image?.mimeType?.startsWith('video/')
        ? 'video'
        : image?.mimeType?.startsWith('audio/')
          ? 'audio'
          : 'image')
    );
  }
  if (token.kind === 'resource' && token.resource) {
    const kind = getMentionResourceMediaKind(token.resource);
    return kind === 'unknown' ? null : kind;
  }
  return null;
}

export function resolveMentionTokenIdentityKey(
  token: MentionToken,
  referenceImages: ReferenceImage[]
): string | null {
  if (token.kind === 'resource' && token.resource) {
    return resolveMentionResourceVideoIdentityKey(token.resource);
  }
  if (token.kind !== 'reference-image') return null;
  const image = referenceImages.find((item) => item.id === token.referenceImageId);
  return (
    resolveVideoReferenceIdentityKey({
      resourceId: image?.resourceId,
      url: image?.remoteUrl || image?.url || token.referenceImageUrl,
    }) ?? (token.referenceImageId ? `image:${token.referenceImageId}` : null)
  );
}

export function countUniqueMentionTokensByKind(params: {
  tokens: MentionToken[];
  referenceImages: ReferenceImage[];
}): Record<ReferenceAssetKind, number> {
  const seen: Record<ReferenceAssetKind, Set<string>> = {
    image: new Set(),
    video: new Set(),
    audio: new Set(),
  };
  const anonymous: Record<ReferenceAssetKind, number> = { image: 0, video: 0, audio: 0 };

  for (const token of params.tokens) {
    const kind = resolveMentionTokenMediaKind(token, params.referenceImages);
    if (!kind) continue;
    const key = resolveMentionTokenIdentityKey(token, params.referenceImages);
    if (key) {
      seen[kind].add(key);
    } else {
      anonymous[kind] += 1;
    }
  }

  return {
    image: seen.image.size + anonymous.image,
    video: seen.video.size + anonymous.video,
    audio: seen.audio.size + anonymous.audio,
  };
}

export function nextMentionKindCounts(params: {
  existingTokens: MentionToken[];
  referenceImages: ReferenceImage[];
  nextKind?: ReferenceAssetKind | null;
  nextIdentityKey?: string | null;
  acceptedIdentityKeysByKind?: Partial<Record<ReferenceAssetKind, string[]>>;
}): Record<ReferenceAssetKind, number> {
  const seen: Record<ReferenceAssetKind, Set<string>> = {
    image: new Set(),
    video: new Set(),
    audio: new Set(),
  };
  const counts: Record<ReferenceAssetKind, number> = { image: 0, video: 0, audio: 0 };

  const occupy = (kind: ReferenceAssetKind, key: string | null | undefined) => {
    if (key) {
      if (seen[kind].has(key)) return;
      seen[kind].add(key);
    }
    counts[kind] += 1;
  };

  for (const token of params.existingTokens) {
    const kind = resolveMentionTokenMediaKind(token, params.referenceImages);
    if (!kind) continue;
    occupy(kind, resolveMentionTokenIdentityKey(token, params.referenceImages));
  }
  for (const kind of ['image', 'video', 'audio'] as const) {
    for (const key of params.acceptedIdentityKeysByKind?.[kind] ?? []) {
      occupy(kind, key);
    }
  }

  if (params.nextKind) {
    const nextKey = params.nextIdentityKey ?? null;
    if (!nextKey || !seen[params.nextKind].has(nextKey)) {
      counts[params.nextKind] += 1;
    }
  }

  return counts;
}

export function wouldExceedMentionKindLimits(params: {
  existingTokens: MentionToken[];
  referenceImages: ReferenceImage[];
  limits: {
    imageMax: number;
    videoMax: number;
    audioMax?: number;
    combinedMax?: number;
  };
  nextKind: ReferenceAssetKind | null;
  nextIdentityKey?: string | null;
  acceptedIdentityKeysByKind?: Partial<Record<ReferenceAssetKind, string[]>>;
}): boolean {
  if (!params.nextKind) return false;
  const counts = nextMentionKindCounts(params);
  if (counts.image > params.limits.imageMax) return true;
  if (counts.video > params.limits.videoMax) return true;
  if (counts.audio > (params.limits.audioMax ?? 0)) return true;
  if (
    params.limits.combinedMax != null &&
    counts.image + counts.video + counts.audio > params.limits.combinedMax
  ) {
    return true;
  }
  return false;
}

export function isSameReferenceAssetIdentity(
  left: Pick<ReferenceImage, 'resourceId' | 'url' | 'remoteUrl'>,
  right: Pick<ReferenceImage, 'resourceId' | 'url' | 'remoteUrl'>
): boolean {
  if (left.resourceId && right.resourceId && left.resourceId === right.resourceId) {
    return true;
  }
  const leftKey = resolveFrameVideoIdentityKey(left);
  const rightKey = resolveFrameVideoIdentityKey(right);
  return Boolean(leftKey && rightKey && leftKey === rightKey);
}

export function collectOccupiedVideoKeysFromReferenceImages(
  images: Array<Pick<ReferenceImage, 'kind' | 'mimeType' | 'resourceId' | 'url' | 'remoteUrl'>>
): string[] {
  return images
    .filter((img) => img.kind === 'video' || Boolean(img.mimeType?.startsWith('video/')))
    .map((img) => resolveFrameVideoIdentityKey(img))
    .filter((key): key is string => Boolean(key));
}

export function canAddVideoMention(params: {
  videoMax: number;
  occupiedVideoKeys: string[];
  existingMentionKeys: string[];
  nextKey: string | null;
}): boolean {
  const unique = new Set(
    [...params.occupiedVideoKeys, ...params.existingMentionKeys].filter(Boolean)
  );
  if (params.nextKey) {
    unique.add(params.nextKey);
  } else {
    return unique.size + 1 <= params.videoMax;
  }
  return unique.size <= params.videoMax;
}

export function countUniqueSubmitItemsByRole(
  items: Array<{ url: string; resource_id?: string; role?: string }>,
  role: string
): number {
  return countUniqueVideoReferenceKeys(
    items
      .filter((item) => item.role === role)
      .map((item) =>
        resolveVideoReferenceIdentityKey({ resourceId: item.resource_id, url: item.url })
      )
  );
}

export function dedupeSubmitReferenceItemsByIdentity<
  T extends { url: string; resource_id?: string; role?: string },
>(items: T[]): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    const role = item.role ?? '';
    if (!DEDUPE_SUBMIT_ROLES.has(role)) {
      result.push(item);
      continue;
    }
    const identity = resolveVideoReferenceIdentityKey({
      resourceId: item.resource_id,
      url: item.url,
    });
    if (identity) {
      const key = `${role}:${identity}`;
      if (seen.has(key)) continue;
      seen.add(key);
    }
    result.push(item);
  }
  return result;
}
